export const googleConsentTypes = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
  'functionality_storage',
  'personalization_storage',
  'security_storage',
] as const

export type GoogleConsentType = (typeof googleConsentTypes)[number]
export type ConsentStatus = 'granted' | 'denied'
export type ConsentCategory = 'ads' | 'analytics' | 'functionality' | 'personalization' | 'security'
export type OptionalConsentCategory = Exclude<ConsentCategory, 'security'>
export type ColorMode = 'auto' | 'light' | 'dark'
export type ModalDisplayMode = 'modal' | 'banner' | 'auto-accept'
export type SettingsButtonPosition = 'none' | 'bottom-left' | 'bottom-right'
export type ModalPresentation = {
  displayMode: ModalDisplayMode
  settingsButtonPosition: SettingsButtonPosition
}
export type ConsentAction = 'accept_all' | 'auto_accept' | 'reject_all' | 'save_preferences' | 'dismissed' | 'withdrawn'

export const modalColorTokens = [
  'primary', 'secondary', 'background', 'textPrimary', 'textSecondary',
] as const

export type ModalColorToken = (typeof modalColorTokens)[number]
export type ModalPalette = Record<ModalColorToken, string>
export type ModalTheme = { light: ModalPalette; dark: ModalPalette }
export type ConsentPreferences = Record<OptionalConsentCategory, boolean>

export type FlatTranslations = Record<string, string>

export interface PublicConfiguration {
  configurationId: string
  policyVersion: string
  presentationVersion: string
  consentMaxAgeSeconds: number
  sdk: { version: string; scriptUrl: string; manifestUrl: string }
  project: {
    name: string
    publicKey: string
    policy: {
      id: string
      availableLanguages: string[]
      googleConsentTypes: GoogleConsentType[]
      privacyPolicyUrl: string | null
      translations: Record<string, FlatTranslations>
      modalTheme: ModalTheme
      modalPresentation: ModalPresentation
    }
  }
}

export interface ConsentState {
  eventId: string
  visitorId: string
  policyVersion: string
  presentationVersion: string
  language: string
  preferences: ConsentPreferences
  googleConsent: Record<GoogleConsentType, ConsentStatus>
  recordedAt: string
  expiresAt: string
}

export interface InitOptions {
  projectKey: string
  apiBaseUrl: string
  language?: string
  colorMode?: ColorMode
  theme?: ModalTheme
  cspNonce?: string
  displayMode?: ModalDisplayMode
  settingsButton?: boolean
  settingsPosition?: SettingsButtonPosition
}

export type RuntimeOptions = Pick<InitOptions, 'language' | 'colorMode' | 'theme' | 'displayMode' | 'settingsButton' | 'settingsPosition'>

export interface CMPEventMap {
  ready: ConsentState | null
  change: ConsentState
  error: Error
}

export interface CMPPublicApi {
  ready: Promise<ConsentState | null>
  init(options: InitOptions): Promise<ConsentState | null>
  update(options: RuntimeOptions): void
  openPreferences(): void
  getConsent(): ConsentState | null
  hasConsent(category: ConsentCategory): boolean
  updateConsent(preferences: Partial<ConsentPreferences>): Promise<ConsentState>
  acceptAll(): Promise<ConsentState>
  rejectAll(): Promise<ConsentState>
  withdrawAll(): Promise<ConsentState>
  renderPreview(theme: ModalTheme, colorMode?: ColorMode, presentation?: ModalPresentation, translations?: FlatTranslations): void
  on<K extends keyof CMPEventMap>(event: K, listener: (payload: CMPEventMap[K]) => void): () => void
}

declare global {
  interface Window {
    CMP: CMPPublicApi
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}
